import React, { FC, useState } from 'react';
import { useModal } from 'store/modal';
import API from 'core/Api';

import { FaTimes } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { createErrorFromResponse } from 'core/Error';
import { multiSignDocAsEmployer } from 'routes/client/documents/helpers';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import { useQueryClient } from 'react-query';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { IRejectDocument, IPropsReasonForm } from '../types';

interface IProps {
  children?: any;
  leaveId: number;
  buttonText: string;
  handlePreview: (leaveId: number) => void;
  invalidate: () => void;
  iconOnly?: boolean;
}

interface IInputs {
  reason: string;
}

interface ISignButtonProps {
  rejectDocument: IRejectDocument;
  queryClient: any;
}

// Reject reason form

const ReasonForm: FC<IPropsReasonForm> = ({ generateRejectDocument, leaveId }: IPropsReasonForm) => {
  const methods = useForm<IInputs>();
  const { t } = useTranslation();

  return (
    <>
      <p className="text-center">{t('documents.table.confirmRejectReasonLabel')}*</p>
      <Form
        methods={methods}
        onSubmit={(data: IInputs) => generateRejectDocument(leaveId, data.reason)}
        btnLabel={t('documents.table.generateRejectDocument')}
        primaryActionBtnType="primary"
        hideCancelBtn
      >
        <FormGroup
          controlId="formGroupReason"
          isRequired
          as="textarea"
          name="reason"
        />
      </Form>
    </>
  );
};

const SignButton: FC<ISignButtonProps> = ({ rejectDocument, queryClient }: ISignButtonProps) => {
  const { showModal } = useModal();
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const [isLoading, setIsLoading] = useState(false);
  return (
    <>
      <Button
        type="button"
        btnType="danger"
        preventDebounce
        loading={isLoading}
        onClick={() => {
          setIsLoading(true);

          if (isLoading) {
            return;
          }

          multiSignDocAsEmployer(
            [rejectDocument.document_holder_id],
            showModal,
            () => queryClient.resetQueries(buildClientEntity('leaves', client)),
            '',
            t
          );
        }}
      >
        {t('leaves.signRejectDocument')}
      </Button>
    </>
  );
};

const RejectLeave: FC<IProps> = ({ leaveId, buttonText, handlePreview, invalidate, iconOnly }: IProps) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReject, setIsLoadingReject] = useState(false);

  // Generate document for rejecetion
  const generateRejectDocument = (id: number, reason: string) =>
    API.get(`leaves/${id}/reject-document?reason=${reason}`)
      .then(data => {
        signRejectDocuments(data.data);
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );

  const signRejectDocuments = (rejectDocument: IRejectDocument) => {
    showModal({
      type: 'form',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('leaves.employer.confirmRejectLeaveMsg')}</p>
          <br />

          <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => {
              handlePreviewDoc({ showModal, id: rejectDocument.id, setIsLoading });
            }}
          >
            {t('documents.table.previewDoc')}
          </Button>

          <hr />

          <SignButton
            rejectDocument={rejectDocument}
            queryClient={queryClient}
          />
        </>
      ),
    });
  };

  // Confirm rejection
  const confirmReject = (id: number) => {
    showModal({
      type: 'form',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('leaves.employer.confirmRejectLeaveMsg')}</p>
          {/* <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoadingReject}
            onClick={() => handlePreviewDoc({ showModal, id, setIsLoading: setIsLoadingReject })}
          >
            {t('documents.table.previewDoc')}
          </Button> */}
          <hr />
          <ReasonForm
            generateRejectDocument={generateRejectDocument}
            leaveId={id}
          />
        </>
      ),
    });
  };

  return (
    <div className="d-inline-block ml-2">
      <TooltipWrapper
        id="tooltip-reject"
        overlay={buttonText}
      >
        <Button
          type="button"
          btnType="danger"
          size="sm"
          onClick={() => confirmReject(leaveId)}
        >
          <FaTimes size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default RejectLeave;
